<template>
  <div class="landing-page">
    <!-- Navigation -->
    <Navbar />

    <!-- Hero Section -->
    <section class="hero-section">
      <a href="/auth/google" class="btn-nav-login mobile-hero-cta">{{ $t('welcome.nav.getStarted') }}</a>
      <div class="container">
        <div class="hero-content hero-content--imgleft">
          <div class="hero-image hero-image-desktop">
            <div class="hero-visual">
              <img src="/img/Mobile-Invoicescan.png" alt="Сканиране на фактура">

              <div class="hfloat hfloat-result">
                <span class="hfloat-ico ok"><font-awesome-icon icon="fa-solid fa-check" /></span>
                <div class="hfloat-txt"><b>{{ $t('welcome.heroExtracted') }}</b></div>
              </div>

              <div class="hfloat hfloat-acc">
                <span class="hfloat-ico acc"><font-awesome-icon icon="fa-solid fa-bullseye" /></span>
                <span>99.5% {{ $t('welcome.heroAccuracy') }}</span>
              </div>

              <div class="hfloat hfloat-speed">
                <span class="hfloat-ico blue"><font-awesome-icon icon="fa-solid fa-bolt" /></span>
                <div class="hfloat-txt"><b>5s</b><i>{{ $t('welcome.heroPerInvoice') }}</i></div>
              </div>
            </div>
          </div>
          <div class="hero-text">
            <span class="hero-kicker">Invoice Automation · AI · OCR</span>
            <h1 class="hero-title">{{ $t('welcome.heroTitle') }}</h1>
            <span class="hero-underline" aria-hidden="true"></span>
            <p class="hero-subtitle">{{ $t('welcome.heroSubtitle') }}</p>
            <div class="hero-buttons">
              <router-link to="/register" class="btn-hero btn-primary">{{ $t('welcome.heroButton') }}</router-link>
              <router-link to="/login" class="btn-hero btn-secondary">{{ $t('auth.login') }}</router-link>
            </div>
            <div class="hero-trust">
              <span class="hero-trust-label">{{ $t('welcome.heroTrust') }}</span>
            </div>
          </div>
        </div>
        <div class="hero-image hero-image-mobile">
          <img src="/img/Mobile-Invoicescan.png" alt="Сканиране на фактура">
        </div>
      </div>
    </section>

    <!-- Benefits Section (pinned card-stack scroll, Contiant-style) -->
    <section class="benefits-section">
      <div class="benefits-sticky">
        <div class="container">
          <div class="benefits-stage">
            <div class="benefits-copy">
              <span class="benefits-eyebrow">{{ $t('welcome.benefitsEyebrow') }}</span>
              <h2 class="section-title">{{ $t('welcome.benefits.title') }}</h2>
              <p class="section-subtitle">{{ $t('welcome.benefits.subtitle') }}</p>
              <router-link to="/contact" class="benefits-cta">{{ $t('welcome.getInTouch') }}</router-link>
            </div>

            <div class="benefits-window">
              <div class="benefit-card">
                <span class="bc-num">01</span>
                <div class="benefit-icon blue"><font-awesome-icon icon="fa-solid fa-wand-magic-sparkles" /></div>
                <h3 class="benefit-title">{{ $t('welcome.benefits.aiExtraction.title') }}</h3>
                <p class="benefit-description">{{ $t('welcome.benefits.aiExtraction.description') }}</p>
              </div>
              <div class="benefit-card">
                <span class="bc-num">02</span>
                <div class="benefit-icon green"><font-awesome-icon icon="fa-solid fa-bolt" /></div>
                <h3 class="benefit-title">{{ $t('welcome.benefits.instantProcessing.title') }}</h3>
                <p class="benefit-description">{{ $t('welcome.benefits.instantProcessing.description') }}</p>
              </div>
              <div class="benefit-card">
                <span class="bc-num">03</span>
                <div class="benefit-icon purple"><font-awesome-icon icon="fa-solid fa-shield-halved" /></div>
                <h3 class="benefit-title">{{ $t('welcome.benefits.secureCompliant.title') }}</h3>
                <p class="benefit-description">{{ $t('welcome.benefits.secureCompliant.description') }}</p>
              </div>
              <div class="benefit-card">
                <span class="bc-num">04</span>
                <div class="benefit-icon orange"><font-awesome-icon icon="fa-solid fa-expand" /></div>
                <h3 class="benefit-title">{{ $t('welcome.benefits.smartScanning.title') }}</h3>
                <p class="benefit-description">{{ $t('welcome.benefits.smartScanning.description') }}</p>
              </div>
              <div class="benefit-card">
                <span class="bc-num">05</span>
                <div class="benefit-icon cyan"><font-awesome-icon icon="fa-solid fa-layer-group" /></div>
                <h3 class="benefit-title">{{ $t('welcome.benefits.multiFormat.title') }}</h3>
                <p class="benefit-description">{{ $t('welcome.benefits.multiFormat.description') }}</p>
              </div>
              <div class="benefit-card">
                <span class="bc-num">06</span>
                <div class="benefit-icon pink"><font-awesome-icon icon="fa-solid fa-clock-rotate-left" /></div>
                <h3 class="benefit-title">{{ $t('welcome.benefits.invoiceTracking.title') }}</h3>
                <p class="benefit-description">{{ $t('welcome.benefits.invoiceTracking.description') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section id="features" class="features-section">
      <div class="container">
        <h2 class="section-title">{{ $t('welcome.features.title') || 'How It Works' }}</h2>
        <p class="section-subtitle">{{ $t('welcome.howItWorks.subtitle') }}</p>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="how-it-works-section">
      <div class="container">
        <div class="steps-timeline">
          <!-- 01 Upload — source formats -->
          <div class="step-item step-blue">
            <div class="step-visual">
              <div class="pg-sources sv-piece">
                <div class="pg-chip"><span class="pg-fmt">PDF</span></div>
                <div class="pg-chip"><span class="pg-fmt">JPG</span></div>
                <div class="pg-chip"><span class="pg-fmt">PNG</span></div>
                <div class="pg-chip"><font-awesome-icon icon="fa-solid fa-mobile-screen-button" /></div>
              </div>
            </div>
            <h3 class="step-title">{{ $t('welcome.howItWorks.step1.title') }}</h3>
          </div>

          <div class="step-arrow">
            <svg viewBox="-20 0 68 38" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M27.751 37.0778L46.0654 19.0651M46.0654 19.0651C41.4718 14.56 32.3442 5.55719 27.7514 1.05205M46.0654 19.0651L-20 19.0654"/></svg>
          </div>

          <!-- 02 Review & Edit — hub logo -->
          <div class="step-item step-violet">
            <div class="step-visual">
              <div class="pg-hub sv-piece"><img src="/img/logo_quare.svg" alt="Invoice Scan" class="pg-hub-logo" /></div>
            </div>
            <h3 class="step-title">{{ $t('welcome.howItWorks.step3.title') }}</h3>
          </div>

          <div class="step-arrow">
            <svg viewBox="-20 0 68 38" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M27.751 37.0778L46.0654 19.0651M46.0654 19.0651C41.4718 14.56 32.3442 5.55719 27.7514 1.05205M46.0654 19.0651L-20 19.0654"/></svg>
          </div>

          <!-- 03 Export & Integrate — extracted result -->
          <div class="step-item step-green">
            <div class="step-visual">
              <div class="pg-card pg-success sv-piece">
                <span class="pg-check"><font-awesome-icon icon="fa-solid fa-check" /></span>
                <b>{{ $t('welcome.pgExtracted') }}</b>
                <i>{{ $t('welcome.pgExtractedDesc') }}</i>
              </div>
            </div>
            <h3 class="step-title">{{ $t('welcome.howItWorks.step4.title') }}</h3>
          </div>
        </div>
      </div>
    </section>

    <!-- Parallax Slider Section (hidden) -->
    <section v-if="false" class="parallax-section">
      <div class="parallax-background" style="background-image: url('/img/acountant-image.jpg')"></div>
      <div class="parallax-overlay"></div>
      <div class="parallax-content">
        <div class="before-after-container">
          <h2 class="before-after-title">{{ $t('welcome.beforeAfter.title') }}</h2>
          <p class="before-after-subtitle">{{ $t('welcome.beforeAfter.subtitle') }}</p>
          
          <div class="before-after-wrapper">
            <div class="before-after-slider" @mousedown="startDrag" @touchstart="startDrag">
              <!-- Before Image -->
              <div class="before-image">
                <img src="/img/before-image.png" alt="Before - Raw Invoice" />
                <div class="image-label before-label">{{ $t('welcome.beforeAfter.beforeLabel') }}</div>
              </div>
              
              <!-- After Image -->
              <div class="after-image" :style="{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }">
                <img src="/img/after-image.png" alt="After - Extracted Data" />
                <div class="image-label after-label">{{ $t('welcome.beforeAfter.afterLabel') }}</div>
              </div>
              
              <!-- Slider Handle -->
              <div class="slider-handle" :style="{ left: `${sliderPosition}%` }">
                <div class="slider-line"></div>
                <div class="slider-button">
                  <svg width="32" height="24" viewBox="0 0 32 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <polyline points="8 18 2 12 8 6"></polyline>
                    <polyline points="24 6 30 12 24 18"></polyline>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Choose / Time Savings — showcase style (Contiant dashboard) -->
    <section class="showcase-section sc-sky">
      <div class="container">
        <div class="sc-row sc-row--imgright">
          <div class="sc-copy">
            <h2 class="sc-title">{{ $t('welcome.saveTitlePre') }} <span class="sc-underline">95%</span><br>{{ $t('welcome.saveTitlePost') }}</h2>
            <h3 class="sc-kick">{{ $t('welcome.saveKick') }}</h3>
            <p class="sc-desc">{{ $t('welcome.saveDesc') }}</p>
          </div>
          <div class="sc-visual">
            <div class="dash">
              <div class="dash-bar">
                <span class="dash-dots"><i></i><i></i><i></i></span>
                <img src="/img/logo_quare.svg" class="dash-logo" alt="" />
                <span class="dash-brand">Invoice Scan</span>
                <span class="dash-user"><span class="dash-ava"></span>Иван</span>
              </div>
              <div class="dash-nav"><span class="active">Спестено време</span><span>Разходи</span><span>Грешки</span></div>
              <div class="dash-analytics">
                <div class="dash-compare">
                  <div class="dc-row">
                    <div class="dc-head"><span class="dc-label">{{ $t('welcome.saveTraditional') }}</span><span class="dc-val dc-bad">{{ $t('welcome.saveTraditionalTime') }}</span></div>
                    <div class="dc-track"><span class="dc-fill dc-fill-bad" style="width:100%"></span></div>
                  </div>
                  <div class="dc-row">
                    <div class="dc-head"><span class="dc-label">{{ $t('welcome.saveWith') }}</span><span class="dc-val dc-good">{{ $t('welcome.saveWithTime') }}</span></div>
                    <div class="dc-track"><span class="dc-fill dc-fill-good" style="width:9%"></span></div>
                  </div>
                </div>
                <div class="dash-calc">
                  <label class="calc-row"><span class="calc-label">{{ $t('welcome.calcInvoices') }}</span><input type="number" v-model.number="invoicesPerMonth" class="calc-input" min="1" max="10000" /></label>
                  <label class="calc-row"><span class="calc-label">{{ $t('welcome.calcMinutes') }}</span><input type="number" v-model.number="timeSavedPerInvoice" class="calc-input" min="1" max="60" /></label>
                </div>
                <div class="dash-stats">
                  <div class="dash-stat"><span class="dash-stat-k">{{ $t('welcome.statFaster') }}</span><b>95%</b></div>
                  <div class="dash-stat"><span class="dash-stat-k">{{ $t('welcome.statHours') }}</span><b>{{ totalHoursSaved }}{{ $t('welcome.unitHours') }}</b></div>
                  <div class="dash-stat"><span class="dash-stat-k">{{ $t('welcome.statCost') }}</span><b>{{ costSavings }}+ {{ $t('welcome.unitMoney') }}</b></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>



    <!-- Showcase: all invoices in one place (Contiant tan dashboard section) -->
    <section class="showcase-section sc-tan">
      <div class="container">
        <div class="sc-row">
          <div class="sc-visual">
            <div class="dash">
              <div class="dash-bar">
                <span class="dash-dots"><i></i><i></i><i></i></span>
                <img src="/img/logo_quare.svg" class="dash-logo" alt="" />
                <span class="dash-brand">Invoice Scan</span>
                <span class="dash-user"><span class="dash-ava"></span>Иван</span>
              </div>
              <div class="dash-nav"><span class="active">Табло</span><span>Фактури</span><span>Доставчици</span><span>Анализ</span></div>
              <div class="dash-thead">
                <span class="th-info">{{ $t('welcome.thInvoice') }}</span><span class="th-amt">{{ $t('welcome.thAmount') }}</span><span class="th-st">{{ $t('welcome.thStatus') }}</span><span class="th-act">{{ $t('welcome.thActions') }}</span>
              </div>
              <div class="dash-rows">
                <div class="dash-r">
                  <span class="r-thumb"><font-awesome-icon icon="fa-solid fa-file-invoice" /></span>
                  <span class="r-info"><b>{{ $t('welcome.invoiceWord') }} #1024</b><i>ACME Ltd · #BG203984</i></span>
                  <span class="r-amt"><b>1 248.00 <small>BGN</small></b><i>{{ $t('welcome.vatLabel') }}: 249.60</i></span>
                  <span class="r-st st-ok">✓ {{ $t('welcome.statusDone') }}</span>
                  <span class="r-act"><i class="ra"><font-awesome-icon icon="fa-solid fa-pen" /></i><i class="ra"><font-awesome-icon icon="fa-solid fa-download" /></i><i class="ra"><font-awesome-icon icon="fa-solid fa-trash" /></i></span>
                </div>
                <div class="dash-r">
                  <span class="r-thumb"><font-awesome-icon icon="fa-solid fa-file-invoice" /></span>
                  <span class="r-info"><b>{{ $t('welcome.invoiceWord') }} #1023</b><i>Balkan Trade · #BG118245</i></span>
                  <span class="r-amt"><b>540.00 <small>BGN</small></b><i>{{ $t('welcome.vatLabel') }}: 108.00</i></span>
                  <span class="r-st st-proc">{{ $t('welcome.statusProcessing') }}</span>
                  <span class="r-act"><i class="ra"><font-awesome-icon icon="fa-solid fa-pen" /></i><i class="ra"><font-awesome-icon icon="fa-solid fa-download" /></i><i class="ra"><font-awesome-icon icon="fa-solid fa-trash" /></i></span>
                </div>
                <div class="dash-r">
                  <span class="r-thumb"><font-awesome-icon icon="fa-solid fa-file-invoice" /></span>
                  <span class="r-info"><b>{{ $t('welcome.invoiceWord') }} #1022</b><i>Orbit Ltd · #BG994017</i></span>
                  <span class="r-amt"><b>3 060.00 <small>BGN</small></b><i>{{ $t('welcome.vatLabel') }}: 612.00</i></span>
                  <span class="r-st st-ok">✓ {{ $t('welcome.statusDone') }}</span>
                  <span class="r-act"><i class="ra"><font-awesome-icon icon="fa-solid fa-pen" /></i><i class="ra"><font-awesome-icon icon="fa-solid fa-download" /></i><i class="ra"><font-awesome-icon icon="fa-solid fa-trash" /></i></span>
                </div>
                <div class="dash-r">
                  <span class="r-thumb"><font-awesome-icon icon="fa-solid fa-file-invoice" /></span>
                  <span class="r-info"><b>{{ $t('welcome.invoiceWord') }} #1021</b><i>Nova Systems · #BG770233</i></span>
                  <span class="r-amt"><b>99.00 <small>BGN</small></b><i>{{ $t('welcome.vatLabel') }}: 19.80</i></span>
                  <span class="r-st st-fail">{{ $t('welcome.statusFailed') }}</span>
                  <span class="r-act"><i class="ra"><font-awesome-icon icon="fa-solid fa-pen" /></i><i class="ra"><font-awesome-icon icon="fa-solid fa-download" /></i><i class="ra"><font-awesome-icon icon="fa-solid fa-trash" /></i></span>
                </div>
                <div class="dash-r">
                  <span class="r-thumb"><font-awesome-icon icon="fa-solid fa-file-invoice" /></span>
                  <span class="r-info"><b>{{ $t('welcome.invoiceWord') }} #1020</b><i>Delta Logic · #BG556128</i></span>
                  <span class="r-amt"><b>1 890.00 <small>BGN</small></b><i>{{ $t('welcome.vatLabel') }}: 378.00</i></span>
                  <span class="r-st st-ok">✓ {{ $t('welcome.statusDone') }}</span>
                  <span class="r-act"><i class="ra"><font-awesome-icon icon="fa-solid fa-pen" /></i><i class="ra"><font-awesome-icon icon="fa-solid fa-download" /></i><i class="ra"><font-awesome-icon icon="fa-solid fa-trash" /></i></span>
                </div>
              </div>
            </div>
          </div>
          <div class="sc-copy">
            <h2 class="sc-title">{{ $t('welcome.tanTitle1') }}<br><span class="sc-underline">{{ $t('welcome.tanTitle2') }}</span></h2>
            <p class="sc-desc">{{ $t('welcome.tanDesc') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Industries — showcase style (Contiant dashboard) -->
    <section id="industries" class="showcase-section sc-lav">
      <div class="container">
        <div class="sc-row sc-row--imgright">
          <div class="sc-copy">
            <h2 class="sc-title">{{ $t('welcome.indTitlePre') }} <span class="sc-underline">{{ $t('welcome.indTitleHl') }}</span></h2>
            <h3 class="sc-kick">{{ $t('welcome.indKick') }}</h3>
            <p class="sc-desc">{{ $t('welcome.indDesc') }}</p>
          </div>
          <div class="sc-visual">
            <div class="dash">
              <div class="dash-bar">
                <span class="dash-dots"><i></i><i></i><i></i></span>
                <img src="/img/logo_quare.svg" class="dash-logo" alt="" />
                <span class="dash-brand">Invoice Scan</span>
                <span class="dash-user"><span class="dash-ava"></span>Иван</span>
              </div>
              <div class="dash-nav"><span class="active">Индустрии</span><span>Доставчици</span><span>Обем</span></div>
              <div class="dash-analytics">
                <div class="dash-industries">
                  <div class="di-row"><span class="di-ico"><font-awesome-icon icon="fa-solid fa-briefcase" /></span><span class="di-name">{{ $t('welcome.indAccounting') }}</span><font-awesome-icon icon="fa-solid fa-check" class="di-tick" /></div>
                  <div class="di-row"><span class="di-ico"><font-awesome-icon icon="fa-solid fa-cart-shopping" /></span><span class="di-name">{{ $t('welcome.indRetail') }}</span><font-awesome-icon icon="fa-solid fa-check" class="di-tick" /></div>
                  <div class="di-row"><span class="di-ico"><font-awesome-icon icon="fa-solid fa-heart-pulse" /></span><span class="di-name">{{ $t('welcome.indHealthcare') }}</span><font-awesome-icon icon="fa-solid fa-check" class="di-tick" /></div>
                  <div class="di-row"><span class="di-ico"><font-awesome-icon icon="fa-solid fa-truck-fast" /></span><span class="di-name">{{ $t('welcome.indLogistics') }}</span><font-awesome-icon icon="fa-solid fa-check" class="di-tick" /></div>
                  <div class="di-row"><span class="di-ico"><font-awesome-icon icon="fa-solid fa-industry" /></span><span class="di-name">{{ $t('welcome.indManufacturing') }}</span><font-awesome-icon icon="fa-solid fa-check" class="di-tick" /></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Developer / API Section (Contiant "Turnkey API solution") -->
    <section class="dev-section">
      <div class="container">
        <div class="dev-card">
          <h2 class="dev-title">{{ $t('welcome.devTitlePre') }} <span class="dev-underline">{{ $t('welcome.devTitleHl') }}</span> {{ $t('welcome.devTitlePost') }}</h2>
          <div class="dev-grid">
            <div class="dev-editor">
              <div class="dev-tabs">
                <span class="dev-dot"></span><span class="dev-dot"></span><span class="dev-dot"></span>
                <span class="dev-tab active">{{ $t('welcome.devTab1') }}</span>
                <span class="dev-tab">{{ $t('welcome.devTab2') }}</span>
              </div>
              <pre class="dev-code"><span class="c-cm"># {{ $t('welcome.devCodeReq') }}</span>
curl -X POST \
  -H <span class="c-st">"Authorization: Bearer </span><span class="c-vr">$&#123;token&#125;</span><span class="c-st">"</span> \
  -F <span class="c-st">"file=@invoice.pdf"</span> \
  https://api.qrme.io/v1/extract

<span class="c-cm">// {{ $t('welcome.devCodeResp') }}</span>
&#123;
  <span class="c-st">"vendor"</span>: <span class="c-st">"ACME Ltd"</span>,
  <span class="c-st">"total"</span>: <span class="c-nm">1248.00</span>,
  <span class="c-st">"vat"</span>: <span class="c-nm">249.60</span>,
  <span class="c-st">"currency"</span>: <span class="c-st">"EUR"</span>,
  <span class="c-st">"confidence"</span>: <span class="c-nm">0.992</span>
&#125;</pre>
            </div>
            <div class="dev-copy">
              <ol class="dev-steps">
                <li><span>01</span> {{ $t('welcome.devStep1') }}</li>
                <li><span>02</span> {{ $t('welcome.devStep2') }}</li>
                <li><span>03</span> {{ $t('welcome.devStep3') }}</li>
              </ol>
              <h3 class="dev-sub">{{ $t('welcome.devSub') }}</h3>
              <p class="dev-desc">{{ $t('welcome.devDesc') }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <h2 class="cta-title">{{ $t('welcome.cta.title') }}</h2>
        <p class="cta-subtitle">{{ $t('welcome.cta.subtitle') }}</p>
        <div class="cta-buttons">
          <router-link to="/register" class="btn-cta btn-primary">
            {{ $t('welcome.cta.button') }}
          </router-link>
          <router-link to="/contact" class="btn-cta btn-secondary">
            {{ $t('welcome.cta.contactSales') }}
          </router-link>
        </div>
      </div>
    </section>

    <!-- FAQ Section (last) -->
    <FAQ></FAQ>

    <!-- Footer -->
    <Footer />
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Navbar from '@/components/Navbar.vue';
import Footer from '@/components/Footer.vue';
import FAQ from "@/components/FAQ.vue";

// Scroll to top when page loads
onMounted(() => {
  window.scrollTo({ top: 0, behavior: 'instant' });
});

const googleLoginUrl = computed(() => {
  return '/auth/google';
});

// Scroll-reveal animations (bidirectional — replay on scroll up & down, Contiant-style)
let revealObserver = null;
onMounted(() => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const selectors = [
    '.hero-text', '.hero-image',
    '.section-title', '.section-subtitle',
    '.steps-timeline', '.video-container', '.before-after-wrapper',
    '.powergrid', '.dev-card', '.sc-copy', '.sc-visual',
    '.ind-item',
    '.time-comparison-card', '.vs-divider', '.savings-stat', '.roi-calculator',
    '.cta-section .container',
  ];
  const els = [];
  selectors.forEach(sel => {
    document.querySelectorAll('.landing-page ' + sel).forEach(el => els.push(el));
  });

  // add base class first
  els.forEach(el => el.classList.add('reveal'));
  // stagger siblings within the same parent (grids, button rows)
  els.forEach(el => {
    const sibs = [...el.parentElement.children].filter(c => c.classList.contains('reveal'));
    const i = sibs.indexOf(el);
    if (i > 0) el.style.transitionDelay = (Math.min(i, 5) * 0.08).toFixed(2) + 's';
  });

  revealObserver = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      e.target.classList.toggle('in-view', e.isIntersecting);
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
  els.forEach(el => revealObserver.observe(el));
});
onUnmounted(() => { if (revealObserver) revealObserver.disconnect(); });

// Benefits — scroll-driven dark card-stack (no scroll-jacking / pinning; compact section)
let benefitsScrollHandler = null;
onMounted(() => {
  if (typeof window === 'undefined') return;
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = window.matchMedia && window.matchMedia('(max-width: 1024px)').matches;
  if (reduce) return; // fallback to static grid
  if (isMobile) {
    // mobile: simple slide-up reveal on the static card list (no pinned stack)
    const t = document.querySelector('.landing-page .benefits-section');
    if (!t) return;
    const cs = [...t.querySelectorAll('.benefit-card')];
    cs.forEach(c => c.classList.add('m-reveal'));
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => e.target.classList.toggle('m-in', e.isIntersecting));
      }, { threshold: 0.18 });
      cs.forEach(c => io.observe(c));
    } else {
      cs.forEach(c => c.classList.add('m-in'));
    }
    return;
  }

  const track = document.querySelector('.landing-page .benefits-section');
  if (!track) return;
  const cards = [...track.querySelectorAll('.benefit-card')];
  if (!cards.length) return;
  const n = cards.length;
  track.classList.add('benefits-animated');

  const apply = (focus) => {
    cards.forEach((card, i) => {
      const d = i - focus;
      const ad = Math.abs(d);
      // spaced wider than a card's height so neighbours never overlap;
      // the centered one is in focus, the others slide through and fade
      const y = d * 310;
      const scale = Math.max(0.9, 1 - ad * 0.05);
      // quadratic falloff: focused card stays bright & readable, neighbours fade fast
      const op = Math.max(0, 1 - ad * ad * 0.9);
      card.style.transform = `translate(-50%, -50%) translateY(${y.toFixed(1)}px) scale(${scale.toFixed(3)})`;
      card.style.opacity = op.toFixed(2);
      card.style.zIndex = String(Math.round(100 - ad * 10));
      card.style.pointerEvents = ad < 0.5 ? 'auto' : 'none';
    });
  };

  let raf = false;
  const update = () => {
    raf = false;
    const total = track.offsetHeight - window.innerHeight;
    if (total <= 0) { apply(0); return; }
    // page is pinned; focus runs 0 → last so every card is seen before scrolling on
    const p = Math.min(1, Math.max(0, -track.getBoundingClientRect().top / total));
    apply(p * (n - 1));
  };
  benefitsScrollHandler = () => { if (!raf) { raf = true; requestAnimationFrame(update); } };
  window.addEventListener('scroll', benefitsScrollHandler, { passive: true });
  window.addEventListener('resize', benefitsScrollHandler, { passive: true });
  update();
});
onUnmounted(() => {
  if (benefitsScrollHandler) {
    window.removeEventListener('scroll', benefitsScrollHandler);
    window.removeEventListener('resize', benefitsScrollHandler);
  }
});

// ROI Calculator
const invoicesPerMonth = ref(250);
const timeSavedPerInvoice = ref(12);
const hourlyRate = ref(25);

const totalHoursSaved = computed(() => {
  const totalMinutes = invoicesPerMonth.value * timeSavedPerInvoice.value;
  return Math.round(totalMinutes / 60);
});

const costSavings = computed(() => {
  return Math.round(totalHoursSaved.value * hourlyRate.value);
});

// Before/After slider functionality
const sliderPosition = ref(50);
const isDragging = ref(false);

const startDrag = (e) => {
  isDragging.value = true;
  updateSliderPosition(e);
};

const updateSliderPosition = (e) => {
  if (!isDragging.value && e.type !== 'mousedown' && e.type !== 'touchstart') return;
  
  const container = e.currentTarget || e.target.closest('.before-after-slider');
  if (!container) return;
  
  const rect = container.getBoundingClientRect();
  const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
  const x = clientX - rect.left;
  const percentage = (x / rect.width) * 100;
  
  sliderPosition.value = Math.max(0, Math.min(100, percentage));
};

const stopDrag = () => {
  isDragging.value = false;
};

const handleMouseMove = (e) => {
  if (isDragging.value) {
    const container = document.querySelector('.before-after-slider');
    if (container) {
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      sliderPosition.value = Math.max(0, Math.min(100, percentage));
    }
  }
};

const handleTouchMove = (e) => {
  if (isDragging.value) {
    const container = document.querySelector('.before-after-slider');
    if (container) {
      const rect = container.getBoundingClientRect();
      const x = e.touches[0].clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      sliderPosition.value = Math.max(0, Math.min(100, percentage));
    }
  }
};

onMounted(() => {
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', stopDrag);
  document.addEventListener('touchmove', handleTouchMove);
  document.addEventListener('touchend', stopDrag);
});

onUnmounted(() => {
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', stopDrag);
  document.removeEventListener('touchmove', handleTouchMove);
  document.removeEventListener('touchend', stopDrag);
});
</script>

<style scoped>
/* ============================================================
   CONTIANT-INSPIRED — light, airy, rounded pastel cards,
   violet accent, dark navy CTA cards, generous whitespace.
   ============================================================ */
.landing-page {
  --bg: #f4f3f8;
  --card: #ffffff;
  --ink: #1f2933;
  --muted: #6b7280;
  --violet: #2b82f0;
  --violet-hover: #1a6fdc;
  --lav: #dcd2f5;
  --peach: #e7d4c9;
  --mint: #cfe4dd;
  --skyblue: #c9def4;
  --dark: #1b2430;
  --line: rgba(31, 41, 51, 0.08);
  --radius: 28px;
  --radius-sm: 18px;
  --pill: 999px;
  --shadow: 0 24px 60px -34px rgba(31, 41, 51, 0.3);

  min-height: 100vh;
  background: var(--bg);
  color: var(--ink);
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  -webkit-font-smoothing: antialiased;
  letter-spacing: -0.01em;
}

.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

.section-title {
  font-size: clamp(34px, 4.6vw, 58px);
  font-weight: 600;
  letter-spacing: -0.035em;
  line-height: 1.06;
  color: var(--ink);
}
.section-subtitle {
  font-size: clamp(18px, 2vw, 22px);
  font-weight: 400;
  color: var(--muted);
  line-height: 1.5;
}

/* Scroll-reveal — fades/rises in on scroll down, reverses on scroll up */
.reveal {
  opacity: 0;
  transform: translateY(44px);
  transition: opacity 0.7s cubic-bezier(0.22, 0.7, 0.2, 1), transform 0.7s cubic-bezier(0.22, 0.7, 0.2, 1);
  will-change: opacity, transform;
}
.reveal.in-view {
  opacity: 1;
  transform: none;
}
@media (prefers-reduced-motion: reduce) {
  .reveal { opacity: 1 !important; transform: none !important; }
}

/* shared pill button */
.btn-hero, .btn-cta {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 14px 26px;
  border-radius: var(--pill);
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  letter-spacing: -0.01em;
  transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease, color 0.2s ease;
  border: 1px solid transparent;
}
.btn-hero.btn-primary, .btn-cta.btn-primary {
  background: var(--violet);
  color: #fff;
  box-shadow: 0 14px 30px -12px rgba(43, 130, 240, 0.7);
}
.btn-hero.btn-primary::after, .btn-cta.btn-primary::after,
.btn-hero.btn-secondary::after {
  content: '→';
  font-size: 1.05em;
  transition: transform 0.2s ease;
}
.btn-hero.btn-primary:hover, .btn-cta.btn-primary:hover {
  background: var(--violet-hover);
  transform: translateY(-2px);
}
.btn-hero.btn-primary:hover::after, .btn-cta.btn-primary:hover::after { transform: translateX(4px); }
.btn-hero.btn-secondary {
  background: var(--card);
  color: var(--ink);
  border: 1px solid var(--line);
}
.btn-hero.btn-secondary:hover { transform: translateY(-2px); border-color: rgba(31,41,51,0.2); }
.btn-hero.btn-secondary:hover::after { transform: translateX(4px); }

/* ===================== HERO ===================== */
.hero-section { position: relative; padding: 56px 0 60px; background: transparent; overflow: clip; overflow-clip-margin: 140px; }
.mobile-hero-cta { display: none; }
.hero-content { display: grid; grid-template-columns: 1.05fr 0.95fr; gap: 48px; align-items: end; }
.hero-content--imgleft { grid-template-columns: 0.95fr 1.05fr; }
.hero-content--imgleft .hero-image img { margin-left: 0; margin-right: auto; }
.hero-content > .hero-text { align-self: center; padding-bottom: 70px; }

.hero-kicker {
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--violet);
  margin-bottom: 18px;
}
.hero-title {
  font-size: clamp(40px, 5vw, 66px);
  font-weight: 600;
  line-height: 1.04;
  letter-spacing: -0.035em;
  color: var(--ink);
  margin: 0 0 6px;
}
.hero-underline {
  display: block;
  width: 220px;
  height: 12px;
  margin: 0 0 26px;
  background: var(--violet);
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='220' height='12'%3E%3Cpath d='M3 8 C 55 3, 110 10, 165 5 S 213 4, 217 7' stroke='black' stroke-width='4' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat;
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='220' height='12'%3E%3Cpath d='M3 8 C 55 3, 110 10, 165 5 S 213 4, 217 7' stroke='black' stroke-width='4' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat;
}
.hero-trust { display: flex; flex-direction: column; gap: 14px; margin-top: 38px; }
.hero-trust-label { font-size: 13px; font-weight: 600; letter-spacing: 0.01em; color: var(--muted); }
.hero-logos { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.hlogo {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 46px; height: 30px; padding: 0 12px;
  border-radius: 8px; background: rgba(31,41,51,0.05);
  font-size: 12px; font-weight: 700; letter-spacing: 0.03em;
  color: rgba(31,41,51,0.45);
}
.hero-subtitle {
  font-size: clamp(17px, 1.9vw, 20px);
  color: var(--muted);
  line-height: 1.55;
  max-width: 48ch;
  margin: 0 0 32px;
}
.hero-buttons { display: flex; gap: 16px; flex-wrap: wrap; align-items: center; }

.hero-image { align-self: end; }
.hero-image-mobile { display: none; }
.hero-image-desktop { display: block; }
.hero-image img {
  width: 100%;
  max-width: 468px;
  height: auto;
  margin-left: auto;
  display: block;
  filter: drop-shadow(0 40px 60px rgba(31, 41, 51, 0.22));
}
/* anchor the hand+phone to the very bottom edge so it reads as "rising from below" */
.hero-image-desktop .hero-visual { align-items: center; margin-bottom: 0; }

/* Hero floating UI cards (Contiant-style scene) */
.hero-visual { position: relative; display: flex; justify-content: center; }
.hfloat {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 13px 16px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 22px 50px -18px rgba(31, 41, 51, 0.28), 0 0 0 1px rgba(31, 41, 51, 0.04);
  z-index: 3;
  animation: hfloatY 6s ease-in-out infinite;
}
.hfloat-txt { display: flex; flex-direction: column; line-height: 1.15; }
.hfloat-txt b { font-size: 15px; font-weight: 700; color: var(--ink); letter-spacing: -0.01em; }
.hfloat-txt i { font-size: 12.5px; font-style: normal; color: var(--muted); }
.hfloat-ico {
  width: 36px; height: 36px; border-radius: 11px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 15px; flex-shrink: 0;
}
.hfloat-ico.ok { background: #22a06b; }
.hfloat-ico.blue { background: var(--violet); }
.hfloat-ico.acc { background: #f59e0b; }
.hfloat-acc { font-size: 14px; font-weight: 600; color: var(--ink); }
.hfloat-dot { width: 9px; height: 9px; border-radius: 50%; background: #22a06b; box-shadow: 0 0 0 4px rgba(34, 160, 107, 0.15); }

.hfloat-result { bottom: 12%; left: -6%; animation-delay: -1s; }
.hfloat-acc { top: 40%; right: -2%; animation-delay: -3s; }
.hfloat-speed { top: 12%; left: -8%; animation-delay: -5s; }

@keyframes hfloatY { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }

/* ===================== FEATURES / VIDEO ===================== */
.features-section { padding: 80px 0 0; text-align: center; }
.features-section .section-title { margin: 0 0 14px; }
.features-section .section-subtitle { margin: 0 auto; max-width: 560px; }
.video-container { display: flex; justify-content: center; padding: 0 20px; }
.monitor-frame-image {
  position: relative;
  width: 100%;
  max-width: 960px;
  aspect-ratio: 16 / 9;
  background: var(--dark);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: var(--shadow);
  display: block;
}
.monitor-content { position: absolute; inset: 0; }
.monitor-content iframe { width: 100%; height: 100%; border: none; display: block; }

/* ===================== POWER GRID (every format → hub → export) ===================== */
.grid-section { padding: 70px 0 90px; text-align: center; overflow: hidden; }
.grid-title { margin: 0 0 64px; }
.powergrid {
  display: grid;
  grid-template-columns: auto 1fr auto 1fr auto;
  align-items: center;
  gap: 0;
  max-width: 1040px;
  margin: 0 auto;
}
.pg-sources { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.pg-sources .pg-chip:nth-child(7) { grid-column: 1 / -1; justify-self: center; }
.pg-chip {
  width: 78px; height: 70px; border-radius: 16px;
  background: var(--card); border: 1px solid var(--line);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 14px 30px -22px rgba(31,41,51,0.4);
  color: var(--ink); font-size: 18px;
}
.pg-fmt { font-size: 13px; font-weight: 800; letter-spacing: 0.04em; color: var(--muted); }
.pg-wires { height: 200px; }
.pg-wires svg { width: 100%; height: 100%; }
.pg-wires path { fill: none; stroke: var(--line); stroke-width: 1.5; }
.pg-wires-r { min-width: 40px; }
.pg-hub {
  width: 84px; height: 84px; border-radius: 24px;
  background: #fff; border: 1px solid var(--line);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 26px 50px -24px rgba(31,41,51,0.45);
}
.pg-hub-logo { width: 46px; height: 46px; transform: translateX(3px); }
.pg-result { display: flex; align-items: center; gap: 16px; justify-self: end; }
.pg-card { background: var(--card); border: 1px solid var(--line); border-radius: 20px; box-shadow: var(--shadow); }
.pg-extract { width: 230px; padding: 20px 22px; text-align: left; }
.pg-row { display: flex; align-items: center; justify-content: space-between; padding: 9px 0; border-bottom: 1px solid var(--line); }
.pg-row:nth-child(3) { border-bottom: none; }
.pg-k { font-size: 13px; color: var(--muted); }
.pg-v { font-size: 14px; font-weight: 700; color: var(--ink); }
.pg-amount { color: var(--violet); }
.pg-export {
  margin-top: 14px; width: 100%; padding: 11px; border: none; cursor: pointer;
  border-radius: var(--pill); background: var(--dark); color: #fff;
  font-size: 13px; font-weight: 600; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
}
.pg-success { width: 170px; padding: 22px 20px; text-align: left; display: flex; flex-direction: column; gap: 6px; }
.pg-check { width: 34px; height: 34px; border-radius: 50%; background: rgba(43,130,240,0.12); color: var(--violet); display: inline-flex; align-items: center; justify-content: center; font-size: 14px; margin-bottom: 6px; }
.pg-success b { font-size: 15px; font-weight: 700; color: var(--ink); }
.pg-success i { font-size: 12px; font-style: normal; color: var(--muted); line-height: 1.4; }

/* ===================== HOW IT WORKS (Contiant "Payment way with" flow cards) ===================== */
.how-it-works-section { padding: 70px 0 100px; }
/* ===== How It Works — sequential reveal (card → card → card, left → right) ===== */
.steps-timeline.reveal { opacity: 1; transform: none; }
.steps-timeline.reveal > * { opacity: 0; transform: translateY(26px); transition: opacity 0.5s cubic-bezier(0.22,0.7,0.2,1), transform 0.55s cubic-bezier(0.22,0.7,0.2,1); }
.steps-timeline.in-view > * { opacity: 1; transform: none; }
.steps-timeline.in-view > *:nth-child(1) { transition-delay: 0.05s; }
.steps-timeline.in-view > *:nth-child(2) { transition-delay: 0.25s; }
.steps-timeline.in-view > *:nth-child(3) { transition-delay: 0.45s; }
.steps-timeline.in-view > *:nth-child(4) { transition-delay: 0.65s; }
.steps-timeline.in-view > *:nth-child(5) { transition-delay: 0.85s; }

/* power-grid pieces placed inside the step cards */
.step-visual .pg-sources { gap: 10px; }
.step-visual .pg-success { box-shadow: 0 16px 34px -24px rgba(31,41,51,0.4); }

/* solid pastel colour per step card (matches the showcase sections) */
.step-item.step-blue   { background: var(--skyblue); border: none; }
.step-item.step-violet { background: var(--lav); border: none; }
.step-item.step-green  { background: var(--mint); border: none; }
.steps-timeline {
  max-width: 1040px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr auto 1fr auto 1fr;
  align-items: center;
  gap: 36px;
}
.step-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  text-align: center;
  aspect-ratio: 1 / 1;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 30px 22px 26px;
  box-shadow: 0 26px 60px -38px rgba(31,41,51,0.32);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.step-item:hover { transform: translateY(-6px); box-shadow: 0 34px 76px -34px rgba(31,41,51,0.38); }

/* visual stage — centered in the upper area, like contiant */
.step-visual {
  position: relative;
  flex: 1;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sv-scene { position: relative; display: flex; align-items: center; justify-content: center; }

/* unified step icon tile */
.sv-tile {
  width: 58px; height: 58px; border-radius: 17px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 22px;
  box-shadow: 0 14px 28px -10px rgba(31,41,51,0.45);
}
.sv-blue { background: linear-gradient(150deg, #5aa2ff, #2b82f0); box-shadow: 0 14px 28px -10px rgba(43,130,240,0.6); }
.sv-dark { background: linear-gradient(150deg, #2b3a4d, #1b2430); box-shadow: 0 14px 28px -10px rgba(27,36,48,0.6); }
.sv-green { background: linear-gradient(150deg, #34c98b, #1f9b6a); box-shadow: 0 14px 28px -10px rgba(34,160,107,0.55); }

.step-title { font-size: clamp(15px, 1.5vw, 20px); font-weight: 600; letter-spacing: -0.03em; color: var(--ink); margin: 0; line-height: 1.15; white-space: nowrap; }
.step-arrow {
  display: flex; align-items: center; justify-content: center;
  color: #b5bcc8;
  width: 78px;
  margin: 0;
}
.step-arrow svg { width: 74px; height: 40px; }

/* ===================== BEFORE / AFTER ===================== */
.parallax-section { position: relative; overflow: hidden; padding: 40px 0 90px; background: transparent; display: block; }
.parallax-background, .parallax-overlay { display: none; }
.parallax-content { position: relative; padding: 0 24px; }
.before-after-container { max-width: 1100px; margin: 0 auto; text-align: center; }
.before-after-title { font-size: clamp(32px, 4vw, 50px); font-weight: 600; letter-spacing: -0.03em; color: var(--ink); margin: 0 0 14px; }
.before-after-subtitle { font-size: clamp(17px, 1.8vw, 20px); color: var(--muted); margin: 0 0 40px; }
.before-after-wrapper { max-width: 1000px; margin: 0 auto; }
.before-after-slider {
  position: relative; width: 100%; height: 560px; overflow: hidden;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  cursor: ew-resize; user-select: none; background: #fff;
}
.before-image, .after-image { position: absolute; inset: 0; width: 100%; height: 100%; }
.before-image img, .after-image img { width: 100%; height: 100%; object-fit: contain; display: block; }
.after-image { clip-path: inset(0 50% 0 0); transition: clip-path 0.05s ease-out; }
.image-label {
  position: absolute; bottom: 24px; padding: 8px 18px;
  background: var(--dark); color: #fff;
  font-size: 13px; font-weight: 600; border-radius: var(--pill); z-index: 5;
}
.before-label { left: 24px; }
.after-label { right: 24px; }
.slider-handle { position: absolute; top: 0; bottom: 0; width: 2px; left: 50%; transform: translateX(-50%); z-index: 10; pointer-events: none; }
.slider-line { position: absolute; top: 0; bottom: 0; left: 50%; transform: translateX(-50%); width: 2px; background: var(--violet); }
.slider-button {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
  width: 52px; height: 52px; border-radius: 50%;
  background: var(--violet); color: #fff;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 8px 20px rgba(43, 130, 240, 0.45);
}
.slider-button svg { width: 24px; height: 24px; }

/* ===================== BENEFITS — pinned dark card-stack (scroll-locked) ===================== */
.benefits-section { position: relative; padding: 36px 0; }
.benefits-section.benefits-animated { height: 240vh; padding: 0; }
.benefits-section.benefits-animated .benefits-sticky {
  position: sticky; top: 0; height: 100vh;
  display: flex; align-items: center;
  padding: 84px 0 24px; box-sizing: border-box;
}

.benefits-stage {
  background: var(--dark);
  border-radius: var(--radius);
  padding: 64px 64px 70px;
  display: flex;
  flex-direction: column;
  gap: 48px;
  overflow: hidden;
  position: relative;
}
.benefits-stage::before {
  content: '';
  position: absolute; left: 50%; top: 50%;
  width: 860px; height: 860px; transform: translate(-50%, -50%);
  background: radial-gradient(circle, rgba(43,130,240,0.28), rgba(43,130,240,0) 62%);
  pointer-events: none; z-index: 0;
}
.benefits-stage > * { position: relative; z-index: 1; }
.benefits-section.benefits-animated .benefits-stage {
  display: grid;
  grid-template-columns: 0.92fr 1.08fr;
  gap: 56px;
  align-items: center;
  align-content: center;
  width: 100%;
  min-height: calc(100vh - 132px);
  padding: 48px 72px;
}

.benefits-copy { text-align: left; }
.benefits-eyebrow {
  display: inline-block; font-size: 13px; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase; color: #8fc6ff; margin-bottom: 16px;
}
.benefits-copy .section-title { color: #fff; margin: 0 0 14px; }
.benefits-copy .section-subtitle { color: rgba(255, 255, 255, 0.6); margin: 0; max-width: 38ch; }

/* card window */
.benefits-window { position: relative; display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.benefits-section.benefits-animated .benefits-window {
  display: block;
  height: 640px;
}

.benefit-card {
  position: relative;
  background: linear-gradient(158deg, #ffffff 0%, #f3f7fd 100%);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 24px;
  padding: 38px 38px 40px;
  text-align: left;
  overflow: hidden;
  box-shadow: 0 30px 70px -28px rgba(0, 0, 0, 0.55);
}
.benefits-section.benefits-animated .benefit-card {
  position: absolute;
  top: 50%; left: 50%;
  width: 100%;
  transform: translate(-50%, -50%);
  will-change: transform, opacity;
  transition: transform 0.55s cubic-bezier(0.22, 0.7, 0.2, 1), opacity 0.45s ease;
}
.bc-num {
  position: absolute; top: 16px; right: 26px;
  font-size: 64px; font-weight: 800; line-height: 1;
  letter-spacing: -0.04em; color: rgba(31, 41, 51, 0.06);
  pointer-events: none;
}
.benefit-icon {
  position: relative;
  width: 58px; height: 58px; border-radius: 17px;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 22px; font-size: 22px; color: #fff;
}
.benefit-icon.blue   { background: linear-gradient(150deg, #5aa2ff, #2b82f0); box-shadow: 0 14px 28px -10px rgba(43,130,240,0.6); }
.benefit-icon.green  { background: linear-gradient(150deg, #34d399, #10b981); box-shadow: 0 14px 28px -10px rgba(16,185,129,0.55); }
.benefit-icon.purple { background: linear-gradient(150deg, #a78bfa, #7c5cff); box-shadow: 0 14px 28px -10px rgba(124,92,255,0.55); }
.benefit-icon.orange { background: linear-gradient(150deg, #fbbf24, #f59e0b); box-shadow: 0 14px 28px -10px rgba(245,158,11,0.55); }
.benefit-icon.cyan   { background: linear-gradient(150deg, #22d3ee, #06b6d4); box-shadow: 0 14px 28px -10px rgba(6,182,212,0.55); }
.benefit-icon.pink   { background: linear-gradient(150deg, #f472b6, #ec4899); box-shadow: 0 14px 28px -10px rgba(236,72,153,0.55); }
.benefit-title { position: relative; font-size: 26px; font-weight: 700; letter-spacing: -0.025em; color: #16273a; margin: 0 0 11px; line-height: 1.12; }
.benefit-description { position: relative; font-size: 15.5px; color: rgba(22, 39, 58, 0.6); line-height: 1.55; margin: 0; }

.benefits-cta {
  display: inline-flex; align-items: center; gap: 9px;
  margin-top: 32px;
  padding: 14px 28px;
  border-radius: var(--pill);
  background: var(--violet);
  color: #fff;
  font-size: 16px; font-weight: 600;
  text-decoration: none;
  transition: transform 0.2s ease, background 0.2s ease;
}
.benefits-cta::after { content: '→'; transition: transform 0.2s ease; }
.benefits-cta:hover { background: var(--violet-hover); transform: translateY(-2px); }
.benefits-cta:hover::after { transform: translateX(4px); }

/* ===================== INDUSTRIES (alternating text + visual blocks) ===================== */
.industries-section { position: relative; padding: 60px 0 100px; text-align: center; overflow: hidden; }
.industries-section .hero-background { display: none; }
.industries-section .section-title { color: var(--ink); margin: 0 0 0; }
.industries-section .section-subtitle { color: var(--muted); margin: 24px auto 0; max-width: 640px; }

/* drawing underline under the centered title */
.section-underline {
  display: block;
  width: 150px; height: 4px;
  margin: 18px auto 0;
  border-radius: 4px;
  background: var(--violet);
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 0.8s cubic-bezier(0.22, 0.7, 0.2, 1) 0.1s;
}
.section-title.in-view + .section-underline { transform: scaleX(1); }

/* clean industry grid (Contiant "Payment evolution for every industry") */
.ind-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 46px 52px; margin-top: 66px; text-align: left; }
.ind-item { }
.ind-ico {
  width: 54px; height: 54px; border-radius: 15px;
  background: rgba(43,130,240,0.1); color: var(--violet);
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 21px; margin-bottom: 20px;
}
.ind-title { font-size: 20px; font-weight: 600; letter-spacing: -0.02em; color: var(--ink); margin: 0 0 10px; line-height: 1.2; }
.ind-desc { font-size: 14.5px; color: var(--muted); line-height: 1.6; margin: 0; }

.industry-blocks { display: flex; flex-direction: column; gap: 56px; margin-top: 80px; }
.industry-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
  text-align: left;
}
.industry-row:nth-child(even) .industry-text { order: 2; }
.industry-row:nth-child(even) .industry-visual { order: 1; }

.industry-kicker {
  display: inline-block; font-size: 13px; font-weight: 700;
  letter-spacing: 0.08em; color: var(--violet); margin-bottom: 14px;
}
.industry-title { font-size: clamp(26px, 2.6vw, 36px); font-weight: 600; letter-spacing: -0.025em; color: var(--ink); margin: 0 0 22px; line-height: 1.08; }
.industry-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 14px; }
.industry-list li { display: flex; align-items: center; gap: 12px; font-size: 16px; color: var(--muted); }
.industry-list li svg {
  flex-shrink: 0; width: 22px; height: 22px; padding: 5px;
  border-radius: 50%; background: rgba(43, 130, 240, 0.12); color: var(--violet);
  font-size: 11px;
}

/* pastel visual panel with a faux dashboard card */
.industry-visual {
  border-radius: var(--radius);
  aspect-ratio: 4 / 3;
  display: flex; align-items: center; justify-content: center;
  padding: 44px;
}
.industry-visual.v-lav { background: var(--lav); }
.industry-visual.v-peach { background: var(--peach); }
.industry-visual.v-mint { background: var(--mint); }
.iv-card {
  width: 100%; max-width: 360px;
  background: #fff;
  border-radius: 18px;
  padding: 26px;
  box-shadow: 0 30px 60px -28px rgba(31, 41, 51, 0.4);
}
.iv-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
.iv-ico {
  width: 52px; height: 52px; border-radius: 15px;
  background: var(--dark); color: #fff;
  display: flex; align-items: center; justify-content: center; font-size: 20px;
}
.iv-num { font-size: 30px; font-weight: 700; color: rgba(31, 41, 51, 0.14); letter-spacing: -0.02em; }
.iv-lines { display: flex; flex-direction: column; gap: 12px; }
.iv-lines span { height: 12px; border-radius: 6px; background: #eef1f6; }
.iv-lines span:nth-child(1) { width: 100%; }
.iv-lines span:nth-child(2) { width: 78%; }
.iv-lines span:nth-child(3) { width: 88%; background: linear-gradient(90deg, var(--violet), #7db0ff); opacity: 0.85; }

/* ===================== WHY CHOOSE / TIME SAVINGS ===================== */
.why-choose-section { padding: 90px 0; background: transparent; }
.why-choose-header { text-align: center; margin-bottom: 56px; }
.why-choose-header .section-title { color: var(--ink); margin: 0 0 14px; }
.why-choose-header .section-subtitle { color: var(--muted); margin: 0 auto; max-width: 700px; }
.time-savings-grid { display: grid; grid-template-columns: 1fr auto 1fr; gap: 24px; margin-bottom: 50px; align-items: stretch; }
.time-comparison-card { border-radius: var(--radius); padding: 40px 34px; border: 1px solid var(--line); background: var(--card); box-shadow: var(--shadow); }
.time-comparison-card.automated { background: var(--dark); border-color: transparent; }
.comparison-header { text-align: center; margin-bottom: 26px; }
.comparison-icon { width: 54px; height: 54px; border-radius: 16px; display: flex; align-items: center; justify-content: center; font-size: 22px; margin: 0 auto 16px; }
.traditional-icon { background: var(--bg); color: var(--muted); }
.automated-icon { background: var(--violet); color: #fff; }
.comparison-title { font-size: 22px; font-weight: 600; letter-spacing: -0.02em; color: var(--ink); margin: 0; }
.automated .comparison-title { color: #fff; }
.comparison-body { text-align: center; }
.time-estimate { margin-bottom: 26px; padding: 22px; background: var(--bg); border-radius: var(--radius-sm); }
.automated .time-estimate { background: rgba(255,255,255,0.08); }
.time-number { display: block; font-size: 52px; font-weight: 700; letter-spacing: -0.03em; line-height: 1; margin-bottom: 8px; color: var(--ink); }
.automated .time-number { color: #fff; }
.time-estimate.highlight .time-number { color: #bfdbfe; }
.time-unit { display: block; font-size: 12px; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: 0.1em; }
.automated .time-unit { color: rgba(255,255,255,0.55); }
.comparison-tasks { list-style: none; padding: 0; margin: 0; text-align: left; }
.comparison-tasks li { display: flex; align-items: center; gap: 12px; padding: 11px 4px; font-size: 15px; color: var(--muted); line-height: 1.45; border-bottom: 1px solid var(--line); }
.comparison-tasks li:last-child { border-bottom: none; }
.automated .comparison-tasks li { color: rgba(255,255,255,0.82); border-bottom-color: rgba(255,255,255,0.1); }
.task-icon { flex-shrink: 0; width: 20px; height: 20px; display: inline-flex !important; align-items: center; justify-content: center; font-size: 17px; }
.comparison-tasks .task-icon.negative { color: #cbd0d6; }
.comparison-tasks .task-icon.positive { display: inline-flex !important; color: #60a5fa; }
.vs-divider { display: flex; align-items: center; justify-content: center; align-self: center; width: 52px; height: 52px; border-radius: 50%; background: var(--violet); color: #fff; font-size: 14px; font-weight: 700; box-shadow: 0 10px 24px -8px rgba(43,130,240,0.6); }

.savings-highlight { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; margin-bottom: 50px; }
.savings-stat { background: var(--card); border-radius: var(--radius); padding: 38px 28px; text-align: center; border: 1px solid var(--line); box-shadow: var(--shadow); transition: transform 0.3s ease; }
.savings-stat:hover { transform: translateY(-6px); }
.stat-icon { width: 46px; height: 46px; margin: 0 auto 16px; border-radius: 50%; background: rgba(43,130,240,0.1); color: var(--violet); display: flex; align-items: center; justify-content: center; font-size: 18px; }
.stat-number { font-size: 44px; font-weight: 700; letter-spacing: -0.03em; color: var(--ink); margin: 0 0 6px; }
.stat-label { font-size: 15px; color: var(--muted); font-weight: 400; margin: 0; }

.roi-calculator { background: var(--card); border-radius: var(--radius); padding: 50px; border: 1px solid var(--line); box-shadow: var(--shadow); }
.roi-title { font-size: 26px; font-weight: 600; letter-spacing: -0.02em; color: var(--ink); margin-bottom: 10px; text-align: center; }
.roi-description { font-size: 16px; color: var(--muted); text-align: center; margin-bottom: 32px; }
.roi-breakdown { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.roi-item { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; background: var(--bg); border-radius: var(--radius-sm); border: none; }
.roi-item.highlight { background: var(--violet); }
.roi-item.highlight .roi-label, .roi-item.highlight .roi-value { color: #fff; }
.roi-label { font-size: 15px; color: var(--muted); font-weight: 500; }
.roi-value { font-size: 20px; font-weight: 700; color: var(--ink); letter-spacing: -0.02em; }
.roi-input { font-size: 20px; font-weight: 700; color: var(--ink); border: none; background: #fff; text-align: right; width: 110px; padding: 6px 10px; border-radius: 10px; transition: box-shadow 0.2s ease; }
.roi-input:focus { outline: none; box-shadow: 0 0 0 2px var(--violet); }
.roi-input::-webkit-inner-spin-button, .roi-input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
.roi-input[type=number] { -moz-appearance: textfield; appearance: textfield; }

/* ===================== DEVELOPER / API (lavender card + code editor) ===================== */
.dev-section { padding: 26px 0; }
.dev-section .container { background: var(--lav); border-radius: var(--radius); padding: 56px; }
.dev-card { background: transparent; border-radius: 0; padding: 0; }
.dev-title { font-size: clamp(28px, 3.4vw, 46px); font-weight: 600; letter-spacing: -0.03em; line-height: 1.1; text-align: center; color: var(--ink); margin: 0 0 48px; }
.dev-underline { position: relative; white-space: nowrap; }
.dev-underline::after { content: ''; position: absolute; left: 0; right: 0; bottom: -2px; height: 7px; background: var(--violet); border-radius: 4px; -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='8'%3E%3Cpath d='M2 5 C 50 2, 100 7, 150 4 S 196 3, 198 5' stroke='black' stroke-width='3' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat center/100% 100%; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='8'%3E%3Cpath d='M2 5 C 50 2, 100 7, 150 4 S 196 3, 198 5' stroke='black' stroke-width='3' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat center/100% 100%; }
.dev-grid { display: grid; grid-template-columns: 1.2fr 0.8fr; gap: 48px; align-items: center; }
.dev-steps { list-style: none; margin: 0 0 28px; padding: 0; display: flex; flex-direction: column; gap: 12px; }
.dev-steps li { display: flex; align-items: center; gap: 12px; font-size: 15px; font-weight: 600; color: var(--ink); }
.dev-steps li span { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 9px; background: rgba(27,36,48,0.08); font-size: 12px; font-weight: 700; color: var(--muted); }
.dev-sub { font-size: 22px; font-weight: 600; letter-spacing: -0.02em; color: var(--ink); margin: 0 0 10px; }
.dev-desc { font-size: 15px; color: rgba(27,36,48,0.62); line-height: 1.55; margin: 0; }
.dev-editor { background: #161d27; border-radius: 18px; overflow: hidden; box-shadow: 0 40px 80px -34px rgba(27,36,48,0.7); }
.dev-tabs { display: flex; align-items: center; gap: 8px; padding: 13px 16px; background: #1e2733; border-bottom: 1px solid rgba(255,255,255,0.06); }
.dev-dot { width: 11px; height: 11px; border-radius: 50%; background: rgba(255,255,255,0.18); }
.dev-tab { margin-left: 6px; font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.4); padding: 4px 12px; border-radius: var(--pill); }
.dev-tab.active { background: var(--violet); color: #fff; }
.dev-tab:first-of-type { margin-left: 14px; }
.dev-code { margin: 0; padding: 22px 24px; font-family: 'SF Mono', ui-monospace, 'Cascadia Code', Menlo, Consolas, monospace; font-size: 13px; line-height: 1.75; color: rgba(255,255,255,0.82); overflow-x: auto; white-space: pre; }
.dev-code .c-cm { color: #6b7689; }
.dev-code .c-st { color: #8fd0a0; }
.dev-code .c-vr { color: #ef8aa0; }
.dev-code .c-nm { color: #79b6ff; }

/* ===================== SHOWCASE (Contiant dashboard sections) ===================== */
.showcase-section { padding: 26px 0; }
.showcase-section .container { background: var(--peach); border-radius: var(--radius); padding: 56px; }
.sc-mint .container { background: var(--mint); }
.sc-sky .container { background: var(--skyblue); }
.sc-lav .container { background: var(--mint); }

/* time-savings comparison bars (in the dashboard visual) */
.dash-compare { display: flex; flex-direction: column; gap: 18px; margin-bottom: 2px; }

/* industry list (no percentages) */
.dash-industries { display: flex; flex-direction: column; gap: 7px; }
.di-row { display: flex; align-items: center; gap: 12px; padding: 11px 13px; border-radius: 11px; background: #f8fafc; border: 1px solid #eef2f6; }
.di-ico { width: 32px; height: 32px; border-radius: 9px; background: #eff6ff; color: #0ea5e9; display: inline-flex; align-items: center; justify-content: center; font-size: 14px; flex-shrink: 0; }
.di-name { flex: 1; font-size: 13.5px; font-weight: 600; color: #0f172a; }
.di-tick { color: #10b981; font-size: 13px; }
.dc-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 9px; }
.dc-label { display: inline-flex; align-items: center; gap: 9px; font-size: 13px; font-weight: 600; color: #0f172a; }
.dc-ico { width: 24px; height: 24px; border-radius: 7px; background: #eff6ff; color: #0ea5e9; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; }
.dc-val { font-size: 12px; font-weight: 700; }
.dc-bad { color: #ef4444; }
.dc-good { color: #10b981; }
.dc-brand { color: #0ea5e9; }
.dc-track { height: 12px; border-radius: 6px; background: #eef2f6; overflow: hidden; }
.dc-fill { display: block; height: 100%; border-radius: 6px; }
.dc-fill-bad { background: linear-gradient(90deg, #fca5a5, #ef4444); }
.dc-fill-good { background: linear-gradient(90deg, #6ee7b7, #10b981); }
.dc-fill-brand { background: linear-gradient(90deg, #38bdf8, #0ea5e9); }

/* interactive calculator inputs inside the dashboard */
.dash-calc { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.calc-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 14px; background: #f8fafc; border: 1px solid #eef2f6; border-radius: 12px; cursor: text; }
.calc-label { font-size: 12px; font-weight: 600; color: #64748b; }
.calc-input {
  width: 62px; text-align: right; border: none; background: #fff;
  border: 1px solid #e2e8f0; border-radius: 8px; padding: 5px 8px;
  font-size: 14px; font-weight: 700; color: #0f172a;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.calc-input:focus { outline: none; border-color: #0ea5e9; box-shadow: 0 0 0 3px rgba(14,165,233,0.15); }
.calc-input::-webkit-inner-spin-button, .calc-input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
.calc-input[type=number] { -moz-appearance: textfield; appearance: textfield; }
.sc-row { display: grid; grid-template-columns: 1.22fr 0.78fr; gap: 52px; align-items: center; }
.sc-row--imgright { grid-template-columns: 0.78fr 1.22fr; }
.sc-copy { padding: 6px 0; }
.sc-title { font-size: clamp(28px, 3vw, 44px); font-weight: 600; letter-spacing: -0.03em; line-height: 1.14; color: var(--ink); margin: 0 0 18px; }
.sc-underline { position: relative; white-space: nowrap; }
.sc-underline::after { content: ''; position: absolute; left: 0; right: 0; bottom: -3px; height: 7px; background: var(--violet); border-radius: 4px; -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='8'%3E%3Cpath d='M2 5 C 50 2, 100 7, 150 4 S 196 3, 198 5' stroke='black' stroke-width='3' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat center/100% 100%; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='8'%3E%3Cpath d='M2 5 C 50 2, 100 7, 150 4 S 196 3, 198 5' stroke='black' stroke-width='3' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat center/100% 100%; }
.sc-kick { font-size: 17px; font-weight: 700; font-style: italic; color: var(--ink); margin: 0 0 12px; }
.sc-desc { font-size: 15px; color: rgba(27,36,48,0.62); line-height: 1.6; margin: 0; max-width: 42ch; }

/* faux app dashboard — mirrors the real Invoice Scan UI (light theme, sky accent, solid status badges) */
.dash { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; box-shadow: 0 44px 84px -36px rgba(15,23,42,0.4); color: #0f172a; }
.dash-bar { display: none; }
.dash-dots { display: flex; gap: 5px; }
.dash-dots i { width: 9px; height: 9px; border-radius: 50%; background: #cbd5e1; }
.dash-logo { width: 17px; height: 17px; margin-left: 8px; }
.dash-brand { font-size: 13px; font-weight: 700; letter-spacing: -0.01em; color: #0f172a; }
.dash-user { margin-left: auto; display: flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 500; color: #64748b; }
.dash-ava { width: 22px; height: 22px; border-radius: 50%; background: linear-gradient(135deg, #38bdf8, #0ea5e9); }
.dash-nav { display: none; }
.dash-nav .active { color: #0f172a; position: relative; }
.dash-nav .active::after { content: ''; position: absolute; left: 0; right: 0; bottom: -12px; height: 2px; background: #0ea5e9; border-radius: 2px; }

/* invoice table */
.dash-thead, .dash-r { display: grid; grid-template-columns: 30px 1fr 120px 96px 80px; align-items: center; gap: 12px; padding: 10px 18px; }
.dash-thead { background: #f8fafc; border-bottom: 2px solid #e2e8f0; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; }
.dash-thead .th-info { grid-column: 1 / 3; }
.dash-thead .th-amt { text-align: right; }
.dash-thead .th-st { text-align: center; }
.dash-thead .th-act { text-align: right; }
.dash-rows { display: flex; flex-direction: column; }
.dash-r { border-bottom: 1px solid #eef2f6; }
.dash-r:last-child { border-bottom: none; }
.r-thumb { width: 30px; height: 36px; border-radius: 6px; background: #eff6ff; color: #0ea5e9; display: inline-flex; align-items: center; justify-content: center; font-size: 13px; }
.r-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.r-info b { font-size: 13px; font-weight: 700; color: #0f172a; }
.r-info i { font-style: normal; font-size: 11.5px; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.r-amt { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
.r-amt b { font-size: 13px; font-weight: 700; color: #0f172a; }
.r-amt small { font-size: 10px; font-weight: 600; color: #94a3b8; }
.r-amt i { font-style: normal; font-size: 10.5px; color: #94a3b8; }
.r-st { justify-self: center; padding: 4px 10px; border-radius: 6px; font-size: 10.5px; font-weight: 600; color: #fff; white-space: nowrap; }
.st-ok { background: #10b981; }
.st-proc { background: #f59e0b; }
.st-fail { background: #ef4444; }
.r-act { justify-self: end; display: inline-flex; gap: 6px; }
.r-act .ra { width: 26px; height: 26px; border-radius: 7px; background: #f1f5f9; color: #64748b; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; }

/* analytics variant — light */
.dash-analytics { padding: 18px; display: flex; flex-direction: column; gap: 14px; }
.dash-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.dash-stat { background: #f8fafc; border: 1px solid #eef2f6; border-radius: 12px; padding: 14px 16px; }
.dash-stat-k { display: block; font-size: 11px; color: #64748b; margin-bottom: 6px; }
.dash-stat b { font-size: 22px; font-weight: 700; letter-spacing: -0.02em; color: #0f172a; }
.dash-chart-row { display: grid; grid-template-columns: 1.6fr 0.85fr; gap: 12px; }
.dash-bars { display: flex; align-items: flex-end; gap: 8px; height: 132px; background: #f8fafc; border: 1px solid #eef2f6; border-radius: 12px; padding: 16px; }
.dash-bars i { flex: 1; min-height: 6px; border-radius: 4px 4px 0 0; background: linear-gradient(180deg, #38bdf8, #0ea5e9); }
.dash-donut { display: flex; align-items: center; justify-content: center; background: #f8fafc; border: 1px solid #eef2f6; border-radius: 12px; }
.donut { position: relative; width: 96px; height: 96px; border-radius: 50%; background: conic-gradient(#0ea5e9 0 53%, #e2e8f0 53% 100%); display: flex; align-items: center; justify-content: center; }
.donut::before { content: ''; position: absolute; inset: 13px; border-radius: 50%; background: #ffffff; }
.donut b { position: relative; font-size: 18px; font-weight: 700; color: #0f172a; }

/* ---- Contiant-style staggered reveal for the code editor + dashboards ---- */
/* code editor slides in from the side after the title */
.dev-card.reveal .dev-editor {
  opacity: 0; transform: translateX(46px) scale(0.96);
  transition: opacity 0.7s cubic-bezier(0.22,0.7,0.2,1) 0.15s, transform 0.7s cubic-bezier(0.22,0.7,0.2,1) 0.15s;
}
.dev-card.in-view .dev-editor { opacity: 1; transform: none; }
.dev-card.reveal .dev-steps li { opacity: 0; transform: translateX(-16px); transition: opacity 0.5s ease, transform 0.5s cubic-bezier(0.22,0.7,0.2,1); }
.dev-card.in-view .dev-steps li { opacity: 1; transform: none; }
.dev-card.in-view .dev-steps li:nth-child(1) { transition-delay: 0.2s; }
.dev-card.in-view .dev-steps li:nth-child(2) { transition-delay: 0.32s; }
.dev-card.in-view .dev-steps li:nth-child(3) { transition-delay: 0.44s; }

/* invoice table rows cascade in */
.sc-visual.reveal .dash-r { opacity: 0; transform: translateY(16px); transition: opacity 0.5s ease, transform 0.55s cubic-bezier(0.22,0.7,0.2,1); }
.sc-visual.in-view .dash-r { opacity: 1; transform: none; }
.sc-visual.in-view .dash-r:nth-child(1) { transition-delay: 0.22s; }
.sc-visual.in-view .dash-r:nth-child(2) { transition-delay: 0.31s; }
.sc-visual.in-view .dash-r:nth-child(3) { transition-delay: 0.40s; }
.sc-visual.in-view .dash-r:nth-child(4) { transition-delay: 0.49s; }
.sc-visual.in-view .dash-r:nth-child(5) { transition-delay: 0.58s; }

/* analytics: stat cards rise, bars grow, donut pops */
.sc-visual.reveal .dash-stat { opacity: 0; transform: translateY(16px); transition: opacity 0.5s ease, transform 0.55s cubic-bezier(0.22,0.7,0.2,1); }
.sc-visual.in-view .dash-stat { opacity: 1; transform: none; }
.sc-visual.in-view .dash-stat:nth-child(1) { transition-delay: 0.2s; }
.sc-visual.in-view .dash-stat:nth-child(2) { transition-delay: 0.3s; }
.sc-visual.in-view .dash-stat:nth-child(3) { transition-delay: 0.4s; }
.sc-visual.reveal .dash-bars i { transform: scaleY(0); transform-origin: bottom; transition: transform 0.65s cubic-bezier(0.22,0.7,0.2,1); }
.sc-visual.in-view .dash-bars i { transform: scaleY(1); }
.sc-visual.in-view .dash-bars i:nth-child(1) { transition-delay: 0.35s; }
.sc-visual.in-view .dash-bars i:nth-child(2) { transition-delay: 0.41s; }
.sc-visual.in-view .dash-bars i:nth-child(3) { transition-delay: 0.47s; }
.sc-visual.in-view .dash-bars i:nth-child(4) { transition-delay: 0.53s; }
.sc-visual.in-view .dash-bars i:nth-child(5) { transition-delay: 0.59s; }
.sc-visual.in-view .dash-bars i:nth-child(6) { transition-delay: 0.65s; }
.sc-visual.in-view .dash-bars i:nth-child(7) { transition-delay: 0.71s; }
.sc-visual.in-view .dash-bars i:nth-child(8) { transition-delay: 0.77s; }
.sc-visual.reveal .dash-donut { opacity: 0; transform: scale(0.85); transition: opacity 0.5s ease 0.5s, transform 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.5s; }
.sc-visual.in-view .dash-donut { opacity: 1; transform: none; }

/* comparison / industry bars grow from the left */
.sc-visual.reveal .dc-fill { transform: scaleX(0); transform-origin: left; transition: transform 0.8s cubic-bezier(0.22,0.7,0.2,1); }
.sc-visual.in-view .dc-fill { transform: scaleX(1); }
.sc-visual.in-view .dc-row:nth-child(1) .dc-fill { transition-delay: 0.30s; }
.sc-visual.in-view .dc-row:nth-child(2) .dc-fill { transition-delay: 0.38s; }
.sc-visual.in-view .dc-row:nth-child(3) .dc-fill { transition-delay: 0.46s; }
.sc-visual.in-view .dc-row:nth-child(4) .dc-fill { transition-delay: 0.54s; }
.sc-visual.in-view .dc-row:nth-child(5) .dc-fill { transition-delay: 0.62s; }
.sc-visual.in-view .dc-row:nth-child(6) .dc-fill { transition-delay: 0.70s; }

/* industry list rows cascade in */
.sc-visual.reveal .di-row { opacity: 0; transform: translateY(14px); transition: opacity 0.5s ease, transform 0.5s cubic-bezier(0.22,0.7,0.2,1); }
.sc-visual.in-view .di-row { opacity: 1; transform: none; }
.sc-visual.in-view .di-row:nth-child(1) { transition-delay: 0.22s; }
.sc-visual.in-view .di-row:nth-child(2) { transition-delay: 0.30s; }
.sc-visual.in-view .di-row:nth-child(3) { transition-delay: 0.38s; }
.sc-visual.in-view .di-row:nth-child(4) { transition-delay: 0.46s; }
.sc-visual.in-view .di-row:nth-child(5) { transition-delay: 0.54s; }
.sc-visual.in-view .di-row:nth-child(6) { transition-delay: 0.62s; }

/* ===================== CTA (dark navy card) ===================== */
.cta-section { padding: 40px 0 110px; }
.cta-section .container {
  position: relative;
  background: var(--dark);
  border-radius: var(--radius);
  padding: 96px 48px;
  text-align: center;
  overflow: hidden;
}
.cta-section .container::before {
  content: '';
  position: absolute; left: 50%; top: 50%;
  width: 760px; height: 760px; transform: translate(-50%, -50%);
  background: radial-gradient(circle, rgba(43,130,240,0.28), rgba(43,130,240,0) 62%);
  pointer-events: none;
}
.cta-section .container > * { position: relative; z-index: 1; }
.cta-title { font-size: clamp(32px, 4vw, 50px); font-weight: 600; letter-spacing: -0.03em; color: #fff; margin: 0 0 16px; }
.cta-subtitle { font-size: clamp(17px, 1.8vw, 20px); color: rgba(255,255,255,0.65); margin: 0 auto 36px; max-width: 580px; line-height: 1.5; }
.cta-buttons { display: flex; gap: 16px; justify-content: center; align-items: center; flex-wrap: wrap; }
.cta-section .btn-primary { background: var(--violet); color: #fff; }
.cta-section .btn-primary:hover { background: var(--violet-hover); transform: translateY(-2px); }
.cta-section .btn-secondary {
  display: inline-flex; align-items: center; gap: 9px;
  padding: 14px 26px; border-radius: var(--pill);
  font-size: 16px; font-weight: 600; text-decoration: none;
  background: transparent; color: #fff; border: 1px solid rgba(255,255,255,0.25);
  transition: all 0.2s ease;
}
.cta-section .btn-secondary:hover { background: rgba(255,255,255,0.1); transform: translateY(-2px); }

/* ===================== COMPONENT OVERRIDES (landing only) ===================== */
.landing-page :deep(.navbar),
.landing-page :deep(.navbar) a,
.landing-page :deep(.navbar) button,
.landing-page :deep(.navbar) input {
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}
.landing-page :deep(.navbar) {
  background: rgba(244, 243, 248, 0.8);
  border-bottom: 1px solid var(--line);
  box-shadow: none;
  -webkit-backdrop-filter: saturate(160%) blur(16px);
  backdrop-filter: saturate(160%) blur(16px);
}
.landing-page :deep(.nav-link) { color: var(--muted); }
.landing-page :deep(.nav-link:hover), .landing-page :deep(.nav-link.router-link-active) { color: var(--ink); }
.landing-page :deep(.language-btn) { background: transparent; border: 1px solid var(--line); color: var(--muted); }
.landing-page :deep(.language-btn:hover) { background: #fff; border-color: var(--violet); color: var(--violet); }
.landing-page :deep(.btn-nav-login) { background: var(--violet); color: #fff; border-radius: var(--pill); box-shadow: none; }
.landing-page :deep(.btn-nav-login:hover) { background: var(--violet-hover); transform: translateY(-2px); }
.landing-page :deep(.mobile-menu-btn) { color: var(--ink); }
.landing-page :deep(.nav-links.mobile-open) { background: #fff; border-top: 1px solid var(--line); }
.landing-page :deep(.language-dropdown) { background: #fff; border: 1px solid var(--line); box-shadow: var(--shadow); }

/* FAQ — refined to match the design language (lighter, premium) */
.landing-page :deep(.faq-section) { padding: 50px 0 100px; background: transparent; }
.landing-page :deep(.faq-section .section-title) { font-size: clamp(32px, 4vw, 50px); font-weight: 600; letter-spacing: -0.035em; color: var(--ink); margin: 0 0 14px; }
.landing-page :deep(.faq-section .section-subtitle) { font-size: clamp(17px, 1.8vw, 20px); color: var(--muted); }
.landing-page :deep(.faq-list) { max-width: 760px; margin-top: 44px; }
.landing-page :deep(.faq-item) {
  background: var(--card); border: 1px solid var(--line); border-radius: 16px;
  margin-bottom: 10px; overflow: hidden;
  box-shadow: 0 12px 32px -26px rgba(31,41,51,0.32);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.landing-page :deep(.faq-item:hover) { border-color: rgba(43,130,240,0.35); }
.landing-page :deep(.faq-item.active) { border-color: var(--violet); box-shadow: 0 18px 44px -26px rgba(43,130,240,0.45); }
.landing-page :deep(.faq-question) {
  padding: 19px 24px; background: transparent; color: var(--ink);
  font-size: 16.5px; font-weight: 600; letter-spacing: -0.01em;
}
.landing-page :deep(.faq-question:hover), .landing-page :deep(.faq-item.active .faq-question) { color: var(--violet); }
.landing-page :deep(.faq-icon) {
  width: 30px; height: 30px; padding: 9px; box-sizing: border-box;
  border-radius: 50%; background: rgba(43,130,240,0.1); color: var(--violet);
  margin-left: 16px; flex-shrink: 0; transition: background 0.2s ease, color 0.2s ease;
}
.landing-page :deep(.faq-item.active .faq-icon) { background: var(--violet); color: #fff; }
.landing-page :deep(.faq-answer) { padding: 0 24px 22px; }
.landing-page :deep(.faq-answer p) { color: var(--muted); font-size: 15px; line-height: 1.65; margin: 0; }

/* Footer — light to match */
.landing-page :deep(.footer) { background: var(--bg); border-top: 1px solid var(--line); }
.landing-page :deep(.footer-logo img) { filter: brightness(0) saturate(100%); }
.landing-page :deep(.footer-description) { color: var(--muted); }
.landing-page :deep(.footer-heading) { color: var(--ink); }
.landing-page :deep(.footer-links a), .landing-page :deep(.footer-links span) { color: var(--muted); }
.landing-page :deep(.footer-links a:hover) { color: var(--violet); }
.landing-page :deep(.social-icon) { background: rgba(31,41,51,0.06); color: var(--muted); }
.landing-page :deep(.social-icon:hover) { background: var(--violet); color: #fff; }
.landing-page :deep(.footer-bottom) { border-top-color: var(--line); }
.landing-page :deep(.footer-bottom p) { color: var(--muted); }
.landing-page :deep(.footer-legal-links a) { color: var(--muted); }
.landing-page :deep(.footer-legal-links a:hover) { color: var(--ink); }
.landing-page :deep(.footer-legal-links .separator) { color: var(--line); }

/* ===================== RESPONSIVE ===================== */
@media (max-width: 1024px) {
  .mobile-hero-cta { display: none; }
  .hero-content { grid-template-columns: 1fr; gap: 30px; }
  .hero-text { order: 1; }
  .hero-image-desktop { display: block; order: 2; }
  .hero-image-mobile { display: none; }
  .hero-image-desktop .hero-visual { max-width: 300px; margin: 0 auto; }
  .hero-image-desktop img { max-width: 300px; }
  .hfloat-result { bottom: 2%; left: 0; }
  .hfloat-acc { top: 46%; right: 0; }
  .hfloat-speed { top: 0; left: 0; }
  .ind-grid { grid-template-columns: 1fr; gap: 30px; margin-top: 44px; }
  .industry-row { grid-template-columns: 1fr; gap: 28px; }
  .industry-row:nth-child(even) .industry-text { order: 0; }
  .industry-row:nth-child(even) .industry-visual { order: 0; }
  .industry-visual { aspect-ratio: 16 / 10; padding: 32px; }
  .industry-blocks { gap: 44px; margin-top: 50px; }
  .benefits-stage { padding: 40px 22px 44px; gap: 30px; }
  .benefits-window { grid-template-columns: 1fr; gap: 16px; }
  .benefits-copy { text-align: center; }
  .benefits-copy .section-subtitle { margin: 0 auto; }
  /* mobile benefit cards: slightly tighter + slide-up reveal on scroll */
  .benefits-window .benefit-card { padding: 30px 28px 32px; }
  .benefit-card.m-reveal { opacity: 0; transform: translateY(30px) scale(0.98); transition: opacity 0.55s ease, transform 0.6s cubic-bezier(0.22,0.7,0.2,1); }
  .benefit-card.m-in { opacity: 1; transform: none; }
  .time-savings-grid { grid-template-columns: 1fr; gap: 18px; }
  .vs-divider { display: none; }
  .savings-highlight { grid-template-columns: 1fr; gap: 18px; }
  .roi-breakdown { grid-template-columns: 1fr; gap: 14px; }
  .before-after-slider { height: 420px; }
  .steps-timeline { grid-template-columns: 1fr; gap: 12px; }
  .step-item { aspect-ratio: 1 / 1; width: 100%; max-width: 280px; justify-self: center; padding: 28px 22px; gap: 18px; }
  .step-visual { min-height: auto; }
  .step-arrow { padding: 4px 0; width: auto; margin: 0 auto; justify-self: center; }
  .step-arrow svg { transform: rotate(90deg); }
  /* source chips → 2×2 square chips inside the smaller card */
  .step-visual .pg-sources { grid-template-columns: repeat(2, 1fr); gap: 10px; width: 100%; max-width: 180px; }
  .step-visual .pg-chip { width: auto; height: auto; aspect-ratio: 1 / 1; }
  .step-visual .pg-fmt { font-size: 12px; }
  .roi-calculator { padding: 34px 22px; }

  /* power grid → stack vertically */
  .powergrid { grid-template-columns: 1fr; gap: 22px; justify-items: center; }
  .pg-wires { display: none; }
  .pg-sources { grid-template-columns: repeat(4, 1fr); gap: 10px; }
  .pg-sources .pg-chip:nth-child(7) { grid-column: auto; }
  .pg-result { flex-direction: column; justify-self: center; }
  .grid-title { margin-bottom: 40px; }

  /* developer card → stack */
  .dev-section .container { padding: 34px 22px; }
  .dev-card { padding: 0; }
  .dev-grid { grid-template-columns: 1fr; gap: 30px; }
  .dev-code { font-size: 12px; }
  /* slide the code editor up (not sideways) so it never causes horizontal scroll */
  .dev-card.reveal .dev-editor { transform: translateY(28px) scale(0.98); }

  /* showcase dashboards → stack */
  .showcase-section .container { padding: 28px 16px; }
  .sc-row, .sc-row--imgright { grid-template-columns: 1fr; gap: 28px; }
  .sc-row--imgright .sc-copy { order: 0; }
  .dash-nav { gap: 12px; overflow-x: auto; }
  .dash-tr { font-size: 11.5px; }
  /* invoice table → fit narrow screens (hide header + actions, auto columns so nothing overlaps) */
  .dash-thead { display: none; }
  .dash-r { grid-template-columns: 20px 1fr auto auto; gap: 9px; padding: 10px 12px; }
  .th-act, .r-act { display: none; }
  .r-st { justify-self: end; font-size: 9.5px; padding: 3px 8px; }
  .r-amt b { font-size: 12.5px; }
  .r-amt small, .r-amt i { font-size: 9.5px; }
  .r-info b { font-size: 12.5px; }
  .r-info i { font-size: 11px; }
}

</style>

